/**
 * Run Q / Q-2 — report a render crash to the server, under the PHI rule.
 *
 * Background. Sentry was removed from this frontend because on any browser
 * error it egressed the React tree (and whatever patient text was rendered in
 * it) to a third party. Nothing replaced it, so a crash left no trace we can
 * read: the Front Desk Confirmations tab threw a ReferenceError on every click
 * from 2026-07-12 to 2026-09-07 and the only evidence in the server logs was
 * the ABSENCE of the request the tab would have made.
 *
 * What this sends is a FIXED shape, five fields, all of them ours:
 *   route       window.location.pathname only — never the query string or hash
 *               (ids in the path are sha8-masked by the server, as morgan does)
 *   frame       the top frame of the React component stack, trimmed to the
 *               component name + file:line
 *   errorClass  the constructor name
 *   message     error.message, truncated to 200 characters HERE and again on
 *               the server
 *   bundle      the index-*.js this page is running, so a crash can be tied to
 *               a deploy
 * No component stack beyond the first frame, no props, no state, no DOM text,
 * no request body. The server re-applies every limit — see
 * backend/src/routes/clientErrors.ts and the canary in
 * backend/tests/run-q-q2-client-errors.test.ts.
 */
import { runningBundle } from '@/lib/versionNudge';
import { endpointClass } from '@/hooks/aiStreamWatchdog';

export const CLIENT_ERROR_PATH = '/api/client-errors';
/** A render loop must not flood the route. Per page load, not per boundary. */
export const MAX_REPORTS_PER_PAGE = 5;
export const MESSAGE_MAX = 200;
const SESSION_KEY = 'msk_client_error_session';

let reportsThisPage = 0;

/** Test seam: reset the per-page counter. */
export function resetClientErrorReporter(): void {
  reportsThisPage = 0;
}

/**
 * A stable-per-tab random id, so the server can rate-limit per session rather
 * than per IP (a whole clinic shares one IP). Not an account id and never
 * stored server-side.
 */
export function sessionId(): string {
  try {
    const store = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    if (!store) return 'nosession';
    const existing = store.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10);
    store.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'nosession';
  }
}

/**
 * The first frame of a React component stack, as `Component (file.tsx:line)`.
 * React's stack is a list of `    at X (url:line:col)` lines; the URL can be a
 * full dev-server URL, so keep only the last path segment.
 */
export function topComponentFrame(componentStack: string | null | undefined): string {
  if (typeof componentStack !== 'string') return '-';
  for (const raw of componentStack.split('\n')) {
    const line = raw.trim();
    if (!line.startsWith('at ')) continue;
    const m = line.match(/^at\s+([A-Za-z0-9_$.]+)\s*(?:\(([^)]*)\))?/);
    if (!m) continue;
    const name = m[1]!;
    const loc = m[2] ?? '';
    const fileLine = loc.match(/([^/\\?#]+\.[a-z]+):(\d+)/i);
    return fileLine ? `${name} (${fileLine[1]}:${fileLine[2]})` : name;
  }
  return '-';
}

export interface ClientErrorReport {
  route: string;
  frame: string;
  errorClass: string;
  message: string;
  bundle: string;
  sessionId: string;
  /** Run U / U-3 — absent for a render crash; 'client_timeout' for an AI watchdog fire. */
  kind?: 'client_timeout';
}

/** Build the report. Exported so the shape can be pinned without a network. */
export function buildClientErrorReport(error: unknown, componentStack?: string | null): ClientErrorReport {
  const loc = (globalThis as { window?: { location?: { pathname?: string } } }).window?.location;
  return {
    route: typeof loc?.pathname === 'string' ? loc.pathname : '-',
    frame: topComponentFrame(componentStack),
    errorClass: error instanceof Error ? error.constructor.name || 'Error' : typeof error,
    message: (error instanceof Error ? error.message : String(error)).slice(0, MESSAGE_MAX),
    bundle: runningBundle() ?? '-',
    sessionId: sessionId(),
  };
}

/**
 * Fire-and-forget. A failure here must never surface: the caller is already
 * handling a crash, and a rejected promise inside componentDidCatch would
 * become an unhandled rejection on top of it.
 */
export function reportClientError(error: unknown, componentStack?: string | null): void {
  send(() => buildClientErrorReport(error, componentStack));
}

/**
 * Run U / U-3 — the report for an AI request whose client watchdog fired. Same
 * five fields plus `kind`; the endpoint travels as a route class with ids
 * removed (`ai/parse-history`), never the raw path.
 */
export function buildClientTimeoutReport(endpoint: string, ms: number): ClientErrorReport & { kind: 'client_timeout' } {
  const loc = (globalThis as { window?: { location?: { pathname?: string } } }).window?.location;
  return {
    kind: 'client_timeout',
    route: typeof loc?.pathname === 'string' ? loc.pathname : '-',
    frame: '-',
    errorClass: 'ClientTimeout',
    message: `AI request watchdog fired after ${ms}ms endpoint=${endpointClass(endpoint)}`.slice(0, MESSAGE_MAX),
    bundle: runningBundle() ?? '-',
    sessionId: sessionId(),
  };
}

/** Fire-and-forget, under the same per-page cap as crash reports. */
export function reportClientTimeout(endpoint: string, ms: number): void {
  send(() => buildClientTimeoutReport(endpoint, ms));
}

function send(build: () => ClientErrorReport): void {
  try {
    if (reportsThisPage >= MAX_REPORTS_PER_PAGE) return;
    reportsThisPage += 1;
    const body = JSON.stringify(build());
    void Promise.resolve(
      fetch(CLIENT_ERROR_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }),
    ).catch(() => {});
  } catch {
    // reporting is never allowed to become the failure
  }
}
